/**
 * Enhanced Admin Logs Type Definitions
 * Comprehensive types for the admin activity logging system
 */

// Log severity levels
export type LogSeverity = 'INFO' | 'WARNING' | 'ERROR' | 'ALERT';

// Action categories for grouping and filtering
export type ActionCategory =
  | 'auth'
  | 'user_management'
  | 'brand'
  | 'report'
  | 'taxonomy'
  | 'import'
  | 'settings'
  | 'organization'
  | 'drawer'
  | 'performance'
  | 'alert'
  | 'other';

// HTTP methods
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// User info in log entries
export interface LogUser {
  id: number;
  email: string;
  name: string;
  role?: 'admin' | 'superadmin' | 'user';
  image?: string | null;
}

// Organization context
export interface LogOrganization {
  id: number;
  name: string;
}

// Action details
export interface LogAction {
  category: ActionCategory;
  type: string;           // e.g., 'login', 'create_user', 'update_brand'
  description: string;    // Human-readable message
}

// Structured change entry (value change OR list add/remove), stored in payload
export interface LogChange {
  label: string;
  from?: string;
  to?: string;
  added?: string[];
  removed?: string[];
}

// Structured detail entry (create/delete), stored in payload
export interface LogDetail {
  label: string;
  value: string;
}

// Request context
export interface LogRequest {
  method: HttpMethod;
  url: string;
  ip?: string;
  userAgent?: string;
  duration?: number;      // milliseconds
  statusCode?: number;
}

// Result of the action
export interface LogResult {
  success: boolean;
  error?: string;
  affectedIds?: (string | number)[];
}

// Main admin log entry interface
export interface AdminLogEntry {
  id: string;
  timestamp: string;
  severity: LogSeverity;
  
  // User context
  user: LogUser;
  organization?: LogOrganization;
  
  // Action details
  action: LogAction;
  
  // Request context (optional)
  request?: LogRequest;
  
  // Raw payload (for detailed view)
  payload?: Record<string, any>;
  
  // Result
  result?: LogResult;
}

// Legacy log entry (from current implementation)
export interface LegacyLogEntry {
  timestamp: string;
  severity: string;
  payload: any;
  user: {
    id: number;
    email: string;
    name: string;
  };
}

// Filter state for logs table
export interface LogsFilterState {
  searchText: string;
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  };
  severity: LogSeverity[];
  categories: ActionCategory[];
  users: number[];             // User IDs
  showOnlyErrors: boolean;
}

// Pagination state
export interface LogsPaginationState {
  pageIndex: number;
  pageSize: number;
  totalItems: number;
}

// Stats for quick metrics cards
export interface LogsStats {
  total: number;
  errors: number;
  warnings: number;
  today: number;
  activeUsers: number;
  /** Distinct action categories in the current result set. */
  categories: number;
  avgResponseTime?: number;
}

// Analytics data structures
export interface LogsActivityByHour {
  hour: number;
  count: number;
  errors: number;
}

export interface LogsActivityByDay {
  date: string;
  count: number;
  errors: number;
}

export interface LogsActionBreakdown {
  category: ActionCategory;
  count: number;
  percentage: number;
}

export interface LogsUserActivity {
  userId: number;
  userName: string;
  userEmail: string;
  actionCount: number;
  errorCount: number;
  lastActive: string;
}

export interface LogsActionTypeBreakdown {
  action: string;
  count: number;
  percentage: number;
}

export interface LogsSeverityBreakdown {
  severity: LogSeverity;
  count: number;
  percentage: number;
}

export interface LogsMetricTrend {
  direction: 'up' | 'down' | 'neutral';
  value: string;
}

export interface LogsMutationBreakdown {
  creates: number;
  updates: number;
  deletes: number;
  other: number;
}

export interface LogsAnalyticsData {
  activityByHour: LogsActivityByHour[];
  activityByDay: LogsActivityByDay[];
  actionBreakdown: LogsActionBreakdown[];
  topActions: LogsActionTypeBreakdown[];
  severityBreakdown: LogsSeverityBreakdown[];
  topUsers: LogsUserActivity[];
  mutations: LogsMutationBreakdown;
  totalActions: number;
  successCount: number;
  errorCount: number;
  warningCount: number;
  activeUsers: number;
  errorRate: number;
  successRate: number;
  avgActionsPerDay: number;
  actionsPerAdmin: number;
  peakHour: number;
  peakHourCount: number;
  periodLabel: string;
  /** Inclusive calendar days in the selected period (or span of loaded data for "all"). */
  daySpan: number;
  /** Earliest / latest timestamps represented in the current slice (ISO). */
  rangeStart?: string;
  rangeEnd?: string;
  /** True when loaded logs don't fully cover the requested calendar period. */
  coverageIncomplete: boolean;
  coverageNote?: string;
  trends: {
    totalActions?: LogsMetricTrend;
    errorRate?: LogsMetricTrend;
    activeUsers?: LogsMetricTrend;
    successRate?: LogsMetricTrend;
  };
}

// Export format options
export type ExportFormat = 'csv' | 'json' | 'xlsx';

export interface ExportOptions {
  format: ExportFormat;
  dateRange?: {
    startDate: Date;
    endDate: Date;
  };
  includePayload: boolean;
  filters?: Partial<LogsFilterState>;
}

// API response types
export interface GetLogsResponse {
  logDetails: AdminLogEntry[];
  oldestTimestamp?: string;  // For timestamp-based pagination
  hasMore?: boolean;         // Whether more logs are available
  stats?: LogsStats;
}

// Category display config
export interface CategoryConfig {
  id: ActionCategory;
  label: string;
  color: string;
  icon: string;
}

// Predefined category configurations
export const CATEGORY_CONFIGS: CategoryConfig[] = [
  { id: 'auth', label: 'Authentication', color: 'blue', icon: 'Lock' },
  { id: 'user_management', label: 'User Management', color: 'purple', icon: 'Users' },
  { id: 'brand', label: 'Brand', color: 'orange', icon: 'Building2' },
  { id: 'report', label: 'Reports', color: 'green', icon: 'FileText' },
  { id: 'taxonomy', label: 'Taxonomy', color: 'cyan', icon: 'Tags' },
  { id: 'import', label: 'Import', color: 'yellow', icon: 'Upload' },
  { id: 'settings', label: 'Settings', color: 'gray', icon: 'Settings' },
  { id: 'organization', label: 'Organization', color: 'pink', icon: 'Building' },
  { id: 'drawer', label: 'Drawer', color: 'indigo', icon: 'LayoutDashboard' },
  { id: 'performance', label: 'Performance', color: 'emerald', icon: 'TrendingUp' },
  { id: 'alert', label: 'Alerts', color: 'red', icon: 'Bell' },
  { id: 'other', label: 'Other', color: 'slate', icon: 'MoreHorizontal' },
];

// Severity display config
export const SEVERITY_CONFIGS: Record<LogSeverity, { label: string; color: string; bgColor: string; icon: string }> = {
  INFO: { label: 'Info', color: 'text-green-700', bgColor: 'bg-green-100', icon: 'Info' },
  WARNING: { label: 'Warning', color: 'text-yellow-700', bgColor: 'bg-yellow-100', icon: 'AlertTriangle' },
  ERROR: { label: 'Error', color: 'text-red-700', bgColor: 'bg-red-100', icon: 'XCircle' },
  ALERT: { label: 'Alert', color: 'text-orange-700', bgColor: 'bg-orange-100', icon: 'AlertCircle' },
};

// Default filter state
export const DEFAULT_FILTERS: LogsFilterState = {
  searchText: '',
  dateRange: {
    startDate: null,
    endDate: null,
  },
  severity: [],
  categories: [],
  users: [],
  showOnlyErrors: false,
};

// Default pagination
export const DEFAULT_PAGINATION: LogsPaginationState = {
  pageIndex: 0,
  pageSize: 25,
  totalItems: 0,
};
